import { useMemo } from 'react';
import { isBoundaryTerritoryHidden } from '~/boundary/settings/boundarySettings.helpers';
import { useBoundaryGroupPermissions } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { displayNameComparer } from '~/store/boundaryItems/boundaryItems.helpers';
import { useMapBoundariesSelector } from '~/store/boundaryItems/boundaryItems.selectors';
import { useBoundaryTerritoryAssignments } from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.selectors';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { isBoundaryTerritoryEmpty } from '../fill/boundaryFill.helpers';

export const useBoundaryItems = ({
  boundaryTerritoryGroup,
}: {
  boundaryTerritoryGroup: BoundaryTerritoryGroup;
}) => {
  const boundaryTerritoryAssignments = useBoundaryTerritoryAssignments();
  const boundaryPermissions = useBoundaryGroupPermissions(boundaryTerritoryGroup.boundaryGroupId);
  const mapBoundaries = useMapBoundariesSelector();

  const { boundaryIsCustom } = boundaryPermissions;

  const displayBoundaryTerritories = !boundaryIsCustom || boundaryTerritoryGroup.settings.boundaryTerritories.length > 0;

  const boundaryTerritoryList = useMemo(() => {
    return boundaryTerritoryGroup.settings.boundaryTerritories.filter(boundaryTerritory => {
      const isEmpty = isBoundaryTerritoryEmpty(boundaryTerritoryAssignments,
        boundaryTerritoryGroup.boundaryTerritoryGroupId,
        boundaryTerritory);
      const isHidden = isBoundaryTerritoryHidden(boundaryTerritory,
        isEmpty,
        boundaryTerritoryGroup.settings.boundaryTerritoryType);

      return !isHidden;
    });
  }, [
    boundaryTerritoryAssignments, boundaryTerritoryGroup.boundaryTerritoryGroupId,
    boundaryTerritoryGroup.settings.boundaryTerritories,
    boundaryTerritoryGroup.settings.boundaryTerritoryType,
  ]);

  const groupBoundaries = mapBoundaries.get(boundaryTerritoryGroup.boundaryGroupId);
  const areGroupBoundariesEmpty = !groupBoundaries || groupBoundaries.size === 0;

  const sortedBoundaryList = useMemo(() => {
    if (!boundaryIsCustom) {
      // we can skip sorting when not custom boundaries, as they are not used and it's performance heavy
      return [];
    }
    return Array.from(groupBoundaries?.values() ?? [])
      .sort(displayNameComparer);
  }, [boundaryIsCustom, groupBoundaries]);

  return {
    displayBoundaryTerritories,
    boundaryTerritoryList,
    sortedBoundaryList,
    areGroupBoundariesEmpty,
    boundaryPermissions,
  };
};
